<template>
  <SfCardInline
    :ui-state="uiState"
    :variant="variant"
    data-testid="document-card"
  >
    <template #left>
      <div class="tw-flex tw-items-center tw-gap-1">
        <div class="tw-flex tw-items-center" :class="documentNameClasses">
          <SfEllipsis :length="300" :tooltip="document.name">{{
            document.name
          }}</SfEllipsis>
        </div>
        <i
          v-if="iconClass"
          v-tooltip="errorMessages"
          class="icon text-sm"
          :class="iconClass"
        ></i>
      </div>
    </template>
    <template #right>
      <div class="tw-flex tw-items-center tw-gap-2">
        <SfEllipsis :length="300" :tooltip="secondaryText">{{
          secondaryText
        }}</SfEllipsis>
        <div class="tw-flex tw-gap-[2px]">
          <i
            v-if="viewingId && allowDownload"
            class="
              sf-pointer
              icon icon-download
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-download"
            @click="handleDownload"
          ></i>
          <i
            v-if="viewingUrl"
            class="
              sf-pointer
              icon icon-eye
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-view"
            @click="handleView"
          ></i>
          <i
            v-if="allowDelete"
            class="
              sf-pointer
              icon icon-trash
              tw-text-xs tw-leading-6 tw-h-6 tw-w-6 tw-text-center
              hover:tw-text-sf-gray-light
            "
            data-testid="document-pill-delete"
            @click="handleDelete"
          ></i>
        </div>
      </div>
    </template>
  </SfCardInline>
</template>

<script setup lang="ts">
import type { StoredDocument } from '../types'
import type { ColorVariantsType, UIStateType } from '@app/shared/constants'
import { isNotProposedDocument } from '../predicates'

import { computed } from 'vue'

import { useDocumentView } from '../composables/use-document-view'
import SfCardInline from '@app/shared/vue/components/sf-card/sf-card-inline.vue'
import SfEllipsis from '@app/shared/vue/components/sf-ellipsis/sf-ellipsis.vue'
import { ColorVariants, UIState } from '@app/shared/constants'
import { isDirectUploadDocument } from '@app/shared/predicates'
import { formatBytes } from '@app/utils/file-helpers'

interface Props {
  allowDelete?: boolean
  allowDownload?: boolean
  document: StoredDocument
  progress?: string
  uiState?: Extract<UIStateType, 'default' | 'loading'>
  variant?: ColorVariantsType
  errorMessages?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  allowDelete: true,
  allowDownload: true,
  errorMessages: () => [],
  progress: undefined,
  uiState: UIState.DEFAULT,
  variant: ColorVariants.DEFAULT,
})

const emit =
  defineEmits<{
    delete: [value: number | string]
  }>()

const documentNameClasses = computed(() => {
  return {
    'tw-line-through': props.variant === ColorVariants.DANGER,
  }
})

const secondaryText = computed(() => {
  if (
    isNotProposedDocument(props.document) &&
    isDirectUploadDocument(props.document)
  ) {
    return formatBytes(props.document.size)
  }

  return props.progress ? `${props.progress}%` : null
})

const iconClass = computed(() => {
  switch (props.variant) {
    case ColorVariants.SUCCESS:
      return 'icon-check-circle'
    case ColorVariants.DANGER:
      return 'icon-warning'
    default:
      return ''
  }
})

const { viewingId, viewingUrl } = useDocumentView(props.document)

function handleDownload() {
  if (!viewingId.value) {
    return
  }

  const url = window.Silverfin.Routes.file_document_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    viewingId.value.toString()
  )

  window.open(url)
}

function handleView() {
  window.open(viewingUrl.value, '_blank')
}

function handleDelete() {
  emit('delete', props.document.id)
}
</script>
